import { STORAGE_KEYS, APP_CONSTANTS, MESSAGING_API_CONSTANTS, SUPPORTED_ENTRY_TYPES, CONVERSATION_CONSTANTS } from './src-config-miaw-constants.js';
import { getSalesforceMessagingUrl, getOrganizationId, getDeploymentDeveloperName } from './src-utils-miaw-chat-config.js';
import { util } from './src-utils-miaw-common.js';
import { getItemInWebStorageByKey, clearWebStorage } from './src-utils-miaw-web-storage-utils.js';

const sendFetchRequest = (apiPath, method, mode, requestHeaders, requestBody) => {
  const messagingJwt = getItemInWebStorageByKey(STORAGE_KEYS.JWT);
  const headers = requestHeaders || {
    "Content-Type": "application/json",
    ...messagingJwt && { Authorization: "Bearer " + messagingJwt }
  };
  const body = requestBody ? JSON.stringify(requestBody) : void 0;
  return fetch(apiPath, {
    method,
    mode,
    headers,
    ...body && { body }
  }).then(async (response) => {
    if (response.status === 401) {
      clearWebStorage();
    }
    if (!response.ok) {
      let responseObject;
      try {
        responseObject = Object.assign(response, await response.json());
      } catch (e) {
        responseObject = Object.assign(response, {
          message: `Error reading the body stream of error object: ${response} ${e}`
        });
      }
      throw responseObject;
    }
    return response;
  });
};
const getUnauthenticatedAccessToken = () => {
  const orgId = getOrganizationId();
  const esDeveloperName = getDeploymentDeveloperName();
  const messagingUrl = getSalesforceMessagingUrl();
  const capabilitiesVersion = APP_CONSTANTS.APP_CAPABILITIES_VERSION;
  const platform = APP_CONSTANTS.APP_PLATFORM;
  const apiPath = `${messagingUrl}/iamessage/api/v2/authorization/unauthenticated/access-token`;
  return sendFetchRequest(
    apiPath,
    "POST",
    "cors",
    {
      "Content-Type": "application/json"
    },
    {
      orgId,
      esDeveloperName,
      capabilitiesVersion,
      platform
    }
  ).then((response) => {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  });
};
const createConversation = (conversationId, routingAttributes) => {
  const esDeveloperName = getDeploymentDeveloperName();
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation`;
  return sendFetchRequest(
    apiPath,
    "POST",
    "cors",
    null,
    {
      ...routingAttributes && { routingAttributes },
      esDeveloperName,
      conversationId
    }
    // 'createNewConversation'
  ).then((response) => {
    if (!response.ok) {
      throw response;
    }
  });
};
const getContinuityJwt = () => {
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/authorization/continuation-access-token`;
  return sendFetchRequest(apiPath, "GET", "cors", null, null).then((response) => {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  });
};
function listConversations(includeClosedConversations = false) {
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/list?inclClosedConvs=${includeClosedConversations}&limit=${MESSAGING_API_CONSTANTS.LIST_CONVERSATION_API_NUM_CONVERSATIONS_LIMIT}`;
  return sendFetchRequest(apiPath, "GET", "cors", null, null).then((response) => {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  });
}
const listConversationEntries = (conversationId, startTimestamp, endTimestamp, direction) => {
  const messagingUrl = getSalesforceMessagingUrl();
  const limitUrlQueryParam = `${`limit=${MESSAGING_API_CONSTANTS.LIST_CONVERSATION_ENTRIES_API_ENTRIES_LIMIT}` }`;
  const startTimestampUrlQueryParam = `${""}`;
  const endTimestampUrlQueryParam = `${""}`;
  const directionUrlQueryParam = `${""}`;
  const entryTypeFilterUrlQueryParam = `${SUPPORTED_ENTRY_TYPES ? `&entryTypeFilter=${SUPPORTED_ENTRY_TYPES}` : ""}`;
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/entries?${limitUrlQueryParam}${startTimestampUrlQueryParam}${endTimestampUrlQueryParam}${directionUrlQueryParam}${entryTypeFilterUrlQueryParam}`;
  return sendFetchRequest(apiPath, "GET", "cors", null, null).then((response) => {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  }).then((responseJson) => {
    const transformedData = [];
    if (typeof responseJson !== "object") {
      throw new Error(`Expected to receive JSON response, instead received ${responseJson}.`);
    }
    if (!Array.isArray(responseJson.conversationEntries)) {
      throw new Error(`Expected entries to be an Array, instead was: ${responseJson.conversationEntries}.`);
    }
    responseJson.conversationEntries.forEach((conversationEntry) => {
      transformedData.push({
        conversationId,
        conversationEntry
      });
    });
    return transformedData;
  });
};
const sendTextMessage = (conversationId, text, messageId, inReplyToMessageId, isNewMessagingSession, routingAttributes, language) => {
  const messagingUrl = getSalesforceMessagingUrl();
  const esDeveloperName = getDeploymentDeveloperName();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/message`;
  return sendFetchRequest(apiPath, "POST", "cors", null, {
    message: {
      ...inReplyToMessageId,
      id: messageId,
      messageType: CONVERSATION_CONSTANTS.MessageTypes.STATIC_CONTENT_MESSAGE,
      staticContent: {
        formatType: CONVERSATION_CONSTANTS.FormatTypes.TEXT,
        text
      }
    },
    ...routingAttributes && { routingAttributes },
    ...isNewMessagingSession && { isNewMessagingSession },
    esDeveloperName,
    ...language
  }).then((response) => {
    if (!response.ok) {
      throw response;
    }
    return response.json();
  });
};
function sendTypingIndicator(conversationId, typingIndicator) {
  const messagingUrl = getSalesforceMessagingUrl();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}/entry`;
  return sendFetchRequest(apiPath, "POST", "cors", null, {
    entryType: typingIndicator,
    id: util.generateUUID()
  });
}
function closeConversation(conversationId) {
  const messagingUrl = getSalesforceMessagingUrl();
  const esDeveloperName = getDeploymentDeveloperName();
  const apiPath = `${messagingUrl}/iamessage/api/v2/conversation/${conversationId}?esDeveloperName=${esDeveloperName}`;
  return sendFetchRequest(apiPath, "DELETE", "cors", null, null);
}

export { closeConversation, createConversation, getContinuityJwt, getUnauthenticatedAccessToken, listConversationEntries, listConversations, sendTextMessage, sendTypingIndicator };
//# sourceMappingURL=src-utils-miaw-api-utils.js.map
